'use client';

import { FunctionComponent, useMemo } from 'react';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import styles from './styles.module.scss';

import { WaitlistButton } from '../WaitlistButton';
import { DateRequestButton } from '../DateRequestButton';
import { RequestGroupBookingButton } from '../RequestGroupBookingButton';
import { getBookingUrl } from '../../../../../../services/getBookingUrl';
import { getOccurrencesWithSpaces } from '../../../../../../services/getOccurrencesWithSpaces';
import { SoldOutButton } from '../SoldOutButton';
import { BookButton } from '../BookButton';
import { useIsEmbed } from '../../../../../../contexts/AppContext/useIsEmbed';
import { useSearchParams } from 'next/navigation';

type InPersonBookButtonProps = {
    bookableOccurrences: Array<PublicOccurrence>;
    course: PublicClassCourse | PublicBlendedCourse;
    educator: PublicEducator;
};

export const InPersonBookButton: FunctionComponent<InPersonBookButtonProps> = ({
    bookableOccurrences,
    course,
    educator,
}) => {
    const isEmbed: boolean = useIsEmbed();
    const searchParams = useSearchParams();
    const location: string | null = useMemo(() => {
        if (searchParams) {
            return searchParams.get('location');
        }
        return null;
    }, [searchParams]);

    const bookButtonLabel: string = useMemo(
        () => (course.application_required === true ? 'Apply' : 'Book'),
        [course.application_required]
    );
    const occurrencesWithSpaces = useMemo<PublicOccurrence[]>(
        () => getOccurrencesWithSpaces(bookableOccurrences),
        [bookableOccurrences]
    );

    const shouldShowDateRequest = useMemo(() => {
        return (course?.enquiry_types || []).includes('date');
    }, [course?.enquiry_types]);

    const shouldShowGroupRequest = useMemo(() => {
        return (course?.enquiry_types || []).includes('group');
    }, [course?.enquiry_types]);

    const hasBookButton = useMemo(() => {
        return bookableOccurrences.length > 0 && occurrencesWithSpaces.length > 0;
    }, [bookableOccurrences.length, occurrencesWithSpaces.length]);

    const requestButtonPreset = hasBookButton ? 'default' : 'primary';

    const requestButtons = useMemo(() => {
        return [
            shouldShowGroupRequest && (
                <RequestGroupBookingButton
                    key="group-request"
                    course={course}
                    educator={educator}
                    isEmbed={isEmbed}
                    preset={requestButtonPreset}
                />
            ),
            shouldShowDateRequest && (
                <DateRequestButton
                    key="date-request"
                    course={course}
                    educator={educator}
                    isEmbed={isEmbed}
                    preset={requestButtonPreset}
                />
            ),
        ].filter(Boolean);
    }, [
        shouldShowGroupRequest,
        shouldShowDateRequest,
        course,
        educator,
        isEmbed,
        requestButtonPreset,
    ]);

    if (bookableOccurrences.length === 0) {
        const primaryButtons = [
            course.waitlist_enabled && (
                <WaitlistButton key="waitlist" course={course} educator={educator} />
            ),
        ].filter(Boolean);

        if (primaryButtons.length > 0 || requestButtons.length > 0) {
            return (
                <div className={styles.buttonsContainer}>
                    {primaryButtons}
                    {requestButtons}
                </div>
            );
        }

        return <p className={styles.noAvailability}>No upcoming dates</p>;
    }

    if (occurrencesWithSpaces.length === 0) {
        // Sold out scenario
        const primaryButton = course.waitlist_enabled ? (
            <WaitlistButton course={course} educator={educator} />
        ) : (
            <SoldOutButton />
        );

        if (requestButtons.length > 0) {
            return (
                <div className={styles.buttonsContainer}>
                    {primaryButton}
                    {requestButtons}
                </div>
            );
        }

        return primaryButton;
    }

    const bookButton = (
        <BookButton
            href={getBookingUrl({
                courseSlug: course.slug,
                courseType: course.type,
                educatorSlug: educator.slug,
                isEmbed,
                location,
            })}
            preset="primary"
            buttonLabel={bookButtonLabel}
        />
    );

    if (requestButtons.length > 0) {
        return (
            <div className={styles.buttonsContainer}>
                {bookButton}
                {requestButtons}
            </div>
        );
    }

    return bookButton;
};
